import {i18n} from '@online-offer/i18n';
import {Grid, GridItem, Show, ThemeIconKey, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, any, values} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {DataColumn, DataColumnItem} from '../../components/DataColumn/DataColumn';
import {SectionPrint} from '../../components/SectionPrint/SectionPrint';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {ElectricalConsumptionData} from '../../utils/getFuelConsumptionData';
import {BadgeLetter, getBadgeLetter} from '../Specification/utils/getBadgeLetter';
import {ElectricalItem} from './components/ElectricalItem';
import {getElectricalParameters} from './utils/getElectricalParameters';

const getElectricalParamBasedOnKey = (
  key: string
): {icon: ThemeIconKey; title: string; labelTooltip?: string | Nullish} => {
  return match<string, {icon: ThemeIconKey; title: string; labelTooltip?: string | Nullish}>(key)
    .with(
      'batteryCapacity',
      always({
        icon: 'electricalParams/battery_capacity',
        title: i18n.t('electricalParameters.item.batteryCapacity'),
      })
    )
    .with(
      'batteryWarranty',
      always({
        icon: 'electricalParams/warranty',
        title: i18n.t('electricalParameters.item.batteryWarranty'),
      })
    )
    .with(
      'electricRange',
      always({
        icon: 'specification/speedometer',
        title: i18n.t('electricalParameters.item.electricRange'),
        labelTooltip: i18n.t('electricalParameters.item.electricRangeTooltip'),
      })
    )
    .with(
      'chargingPowerAc',
      always({
        icon: 'electricalParams/charging',
        title: i18n.t('electricalParameters.item.chargingPowerAc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingPowerAcTooltip'),
      })
    )
    .with(
      'chargingPowerDc',
      always({
        icon: 'electricalParams/charging',
        title: i18n.t('electricalParameters.item.chargingPowerDc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingPowerDcTooltip'),
      })
    )
    .with(
      'electricEnginePower',
      always({
        icon: 'electricalParams/voltage',
        title: i18n.t('electricalParameters.item.electricEnginePower'),
        labelTooltip: i18n.t('electricalParameters.item.electricEnginePowerTooltip'),
      })
    )
    .with(
      'chargingTimeAc',
      always({
        icon: 'electricalParams/connector_ac',
        title: i18n.t('electricalParameters.item.chargingTimeAc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingTimeAcTooltip'),
      })
    )
    .with(
      'chargingTimeDc',
      always({
        icon: 'electricalParams/connector_dc',
        title: i18n.t('electricalParameters.item.chargingTimeDc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingTimeDcTooltip'),
      })
    )
    .with(
      'batteryVoltage',
      always({
        icon: 'electricalParams/voltage',
        title: i18n.t('electricalParameters.item.batteryVoltage'),
        labelTooltip: i18n.t('electricalParameters.item.batteryVoltageTooltip'),
      })
    )
    .with(
      'carbonDioxideEmissionDischarged',
      always({
        icon: 'specification/co2',
        title: i18n.t('electricalParameters.dataColumn.co2EmissionDischarged'),
      })
    )
    .with(
      'co2ClassDischarged',
      always({
        icon: 'specification/energy_savings_leaf',
        title: i18n.t('electricalParameters.dataColumn.co2ClassDischarged'),
      })
    )
    .otherwise(() => ({
      icon: 'electricalParams/voltage',
      title: key,
    }));
};

function GridElectricalItem({
  icon,
  categoryTitle,
  value,
  labelTooltip,
  badge,
}: {
  value: string | Nullish;
  categoryTitle: string;
  icon: ThemeIconKey;
  labelTooltip?: string | Nullish;
  badge?: BadgeLetter | Nullish;
}) {
  if (isNilOrEmpty(value)) {
    return null;
  }
  return (
    <GridItem>
      <ElectricalItem
        value={value!}
        categoryTitle={categoryTitle}
        icon={icon}
        labelTooltip={labelTooltip}
        badge={badge}
      />
    </GridItem>
  );
}

function getElectricalConsumptionItems(
  electricalConsumptionData: ElectricalConsumptionData | Nullish
): DataColumnItem[] {
  if (!electricalConsumptionData) {
    return [];
  }

  return match(electricalConsumptionData)
    .with({schema: 'WLTP'}, (wltpData) => [
      {
        label: i18n.t('electricalParameters.consumption.wltp.combined'),
        value: wltpData.combined,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.cityCenter'),
        value: wltpData.cityCenter,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.outskirts'),
        value: wltpData.outskirts,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.countryRoads'),
        value: wltpData.countryRoads,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.highway'),
        value: wltpData.highway,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.weightedCombined'),
        value: wltpData.weightedCombined,
      },
    ])
    .with({schema: 'NEDC'}, (nedcData) => [
      {
        label: i18n.t('electricalParameters.consumption.nedc.combined'),
        value: nedcData.combined,
      },
    ])
    .exhaustive();
}

export function ElectricalParametersPrint() {
  const {vehicle, alphaCatalogue} = useGetOnlineOfferData();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const data = getElectricalParameters(vehicle, alphaCatalogue, formatDateTime, formatNumber);

  if (!vehicle || !data || !any(isNotNilOrEmpty, values(data))) {
    return null;
  }

  const {electricalData, additionalElectricalData, electricalConsumptionData} = data;

  if (
    !any(isNotNilOrEmpty, values(electricalData)) &&
    !any(isNotNilOrEmpty, values(additionalElectricalData)) &&
    !electricalConsumptionData
  ) {
    return null;
  }

  const electricalConsumptionItems = getElectricalConsumptionItems(electricalConsumptionData);

  return (
    <SectionPrint heading={i18n.t('electricalParameters.heading')}>
      <VStack spacing={8}>
        <Grid columns={2} verticalSpacing={8}>
          {Object.entries(electricalData)
            .filter(([_, value]) => value !== null)
            .map(([key, value]) => {
              const electricalItem = getElectricalParamBasedOnKey(key);
              const badge =
                key === 'carbonDioxideEmissionDischarged'
                  ? getBadgeLetter(vehicle.carbonDioxideEmissionDischarged)
                  : null;
              return (
                <GridElectricalItem
                  key={key}
                  value={value}
                  categoryTitle={electricalItem.title}
                  icon={electricalItem.icon}
                  labelTooltip={electricalItem.labelTooltip}
                  badge={badge}
                />
              );
            })}
        </Grid>

        <Show when={any(isNotNilOrEmpty, values(additionalElectricalData))}>
          <Grid columns={1}>
            <GridItem>
              <DataColumn
                data={[
                  {
                    label: i18n.t('electricalParameters.dataColumn.rangeExtender'),
                    value:
                      additionalElectricalData.hasRangeExtender === true
                        ? i18n.t('general.labels.yes')
                        : null,
                  },
                  {
                    label: i18n.t('electricalParameters.dataColumn.batteryType'),
                    value: additionalElectricalData.batteryType,
                  },
                  {
                    label: i18n.t('electricalParameters.dataColumn.chargingConnectorType'),
                    value: additionalElectricalData.chargingConnectorType,
                  },
                  {
                    label: i18n.t('electricalParameters.dataColumn.numberOfBatteries'),
                    value: additionalElectricalData.numberOfBatteries,
                  },
                  {
                    label: i18n.t('electricalParameters.dataColumn.batteryOwnershipType'),
                    value: additionalElectricalData.batteryOwnershipType,
                    labelTooltip: i18n.t(
                      'electricalParameters.dataColumn.batteryOwnershipTypeTooltip'
                    ),
                  },
                  {
                    label: i18n.t('electricalParameters.dataColumn.biDirectionalCharging'),
                    value: additionalElectricalData.biDirectionalCharging,
                    labelTooltip: i18n.t(
                      'electricalParameters.dataColumn.biDirectionalChargingTooltip'
                    ),
                  },
                  {
                    label: i18n.t('electricalParameters.dataColumn.actualBatteryRange'),
                    value: additionalElectricalData.actualBatteryRange,
                  },
                  ...electricalConsumptionItems,
                ]}
              />
            </GridItem>
          </Grid>
        </Show>
      </VStack>
    </SectionPrint>
  );
}
